import { useEffect, useRef, useState } from "react";
import {
  createOfficeHolder,
  deleteOfficeHolder,
  subscribeToOfficeHolders,
  updateOfficeHolder,
} from "../../services/officeService";

const initialState = {
  nameHindi: "",
  nameEnglish: "",
  roleHindi: "",
  roleEnglish: "",
  order: "",
  photoUrl: "",
  imageFile: null,
  removeImage: false,
};

const OfficeHoldersManager = () => {
  const [members, setMembers] = useState([]);
  const [formState, setFormState] = useState(initialState);
  const [editingId, setEditingId] = useState(null);
  const [message, setMessage] = useState("");
  const [saving, setSaving] = useState(false);
  const [previewUrl, setPreviewUrl] = useState("");
  const fileInputRef = useRef(null);

  useEffect(() => {
    const unsubscribe = subscribeToOfficeHolders((items) => setMembers(items));
    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, []);

  const resetForm = () => {
    setFormState(initialState);
    setEditingId(null);
    setPreviewUrl("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormState((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (event) => {
    const file = event.target.files ? event.target.files[0] : null;
    setFormState((prev) => ({
      ...prev,
      imageFile: file,
      removeImage: file ? false : prev.removeImage,
    }));
  };

  const handleRemovePhoto = () => {
    setFormState((prev) => ({
      ...prev,
      imageFile: null,
      photoUrl: "",
      removeImage: true,
    }));
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  useEffect(() => {
    if (!formState.imageFile) {
      setPreviewUrl("");
      return undefined;
    }
    const objectUrl = URL.createObjectURL(formState.imageFile);
    setPreviewUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [formState.imageFile]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setMessage("");
    const payload = {
      nameHindi: formState.nameHindi,
      nameEnglish: formState.nameEnglish,
      roleHindi: formState.roleHindi,
      roleEnglish: formState.roleEnglish,
      order: formState.order,
    };
    if (formState.imageFile) {
      payload.imageFile = formState.imageFile;
    }
    if (formState.removeImage) {
      payload.removeImage = true;
    }
    try {
      if (editingId) {
        await updateOfficeHolder(editingId, payload);
        setMessage("पदाधिकारी अद्यतन हुआ। / Office bearer updated.");
      } else {
        await createOfficeHolder(payload);
        setMessage("नया पदाधिकारी जोड़ा गया। / Office bearer added.");
      }
      resetForm();
    } catch (error) {
      setMessage(`त्रुटि: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (member) => {
    setEditingId(member.id);
    setFormState({
      nameHindi: member.nameHindi || "",
      nameEnglish: member.nameEnglish || "",
      roleHindi: member.roleHindi || "",
      roleEnglish: member.roleEnglish || "",
      order: member.order || "",
      photoUrl: member.photoUrl || "",
      imageFile: null,
      removeImage: false,
    });
    setPreviewUrl("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleDelete = async (memberId) => {
    if (
      window.confirm(
        "क्या आप इस पदाधिकारी को हटाना चाहते हैं? / Delete this office bearer?",
      )
    ) {
      setSaving(true);
      try {
        await deleteOfficeHolder(memberId);
        setMessage("पदाधिकारी हटाया गया। / Office bearer removed.");
        if (editingId === memberId) {
          resetForm();
        }
      } catch (error) {
        setMessage(`त्रुटि: ${error.message}`);
      } finally {
        setSaving(false);
      }
    }
  };

  return (
    <section className="admin-section">
      <header>
        <h3>पदाधिकारी प्रबंधन</h3>
        <span className="caption">Office Bearers Management</span>
      </header>

      <form onSubmit={handleSubmit} className="admin-form">
        <label htmlFor="member-name-hi">
          नाम (हिंदी)
          <span className="caption">Name (Hindi)</span>
        </label>
        <input
          id="member-name-hi"
          name="nameHindi"
          value={formState.nameHindi}
          onChange={handleChange}
          required
        />

        <label htmlFor="member-name-en">
          नाम (अंग्रेज़ी)
          <span className="caption">Name (English)</span>
        </label>
        <input
          id="member-name-en"
          name="nameEnglish"
          value={formState.nameEnglish}
          onChange={handleChange}
          required
        />

        <label htmlFor="member-role-hi">
          दायित्व (हिंदी)
          <span className="caption">Role (Hindi)</span>
        </label>
        <input
          id="member-role-hi"
          name="roleHindi"
          value={formState.roleHindi}
          onChange={handleChange}
          required
        />

        <label htmlFor="member-role-en">
          दायित्व (अंग्रेज़ी)
          <span className="caption">Role (English)</span>
        </label>
        <input
          id="member-role-en"
          name="roleEnglish"
          value={formState.roleEnglish}
          onChange={handleChange}
          required
        />

        <label htmlFor="member-order">
          क्रमांक (वैकल्पिक)
          <span className="caption">Order (Optional)</span>
        </label>
        <input
          id="member-order"
          name="order"
          type="number"
          value={formState.order}
          onChange={handleChange}
        />

        <label htmlFor="member-photo">
          पदाधिकारी चित्र
          <span className="caption">Office Bearer Photo</span>
        </label>
        <input
          id="member-photo"
          type="file"
          accept="image/*"
          onChange={handleFileChange}
          ref={fileInputRef}
        />
        {(formState.photoUrl || formState.imageFile) && (
          <div className="preview-image">
            <img
              src={formState.imageFile ? previewUrl : formState.photoUrl}
              alt={
                formState.nameEnglish || formState.nameHindi || "Office bearer"
              }
            />
            <button
              type="button"
              onClick={handleRemovePhoto}
              className="danger"
            >
              चित्र हटाएँ
              <span className="caption">Remove Photo</span>
            </button>
          </div>
        )}

        <div className="form-actions">
          <button type="submit" disabled={saving}>
            {editingId ? "अद्यतन करें" : "जोड़ें"}
            <span className="caption">{editingId ? "Update" : "Add"}</span>
          </button>
          {editingId && (
            <button type="button" onClick={resetForm}>
              निरस्त करें
              <span className="caption">Cancel</span>
            </button>
          )}
        </div>
        {message && <p className="form-message">{message}</p>}
      </form>

      <div className="admin-list">
        {members.map((member) => (
          <article key={member.id} className="admin-card">
            <header>
              <h4>{member.nameHindi}</h4>
              <span className="caption">{member.nameEnglish}</span>
            </header>
            <p>{member.roleHindi}</p>
            <span className="caption">{member.roleEnglish}</span>
            {member.photoUrl && (
              <div className="admin-card-image">
                <img
                  src={member.photoUrl}
                  alt={member.nameEnglish || member.nameHindi}
                />
              </div>
            )}
            <div className="card-actions">
              <button type="button" onClick={() => handleEdit(member)}>
                संपादन
                <span className="caption">Edit</span>
              </button>
              <button
                type="button"
                onClick={() => handleDelete(member.id)}
                className="danger"
              >
                हटाएँ
                <span className="caption">Delete</span>
              </button>
            </div>
          </article>
        ))}
        {members.length === 0 && <p>कोई पदाधिकारी सूचीबद्ध नहीं है।</p>}
      </div>
    </section>
  );
};

export default OfficeHoldersManager;
